import ExcelJS from 'exceljs';
import sapMasterList from '../data/sapMasterList.json';

// Helper to find the price in the master list
const findEquipmentData = (searchKey) => {
  if (!searchKey) return null;
  const lowerKey = searchKey.toLowerCase();
  
  // Try exact match first
  let match = sapMasterList.find(item => item['£ KEY']?.toLowerCase() === lowerKey);
  
  // If not found, try partial match
  if (!match) {
    match = sapMasterList.find(item => item['£ KEY']?.toLowerCase().includes(lowerKey));
  }
  
  return match || null;
};

// ── Equipment selection model ────────────────────────────────
// Equipment is picked for the full SOW (scope of work), not per line:
//   selections = {
//     shared: { [catId]: { [groupName]: Set<item> } },          // once, all lines
//     lines:  { [lineIdx]: { [catId]: { [groupName]: Set } } }, // on-pipe only
//   }
// The legacy per-line shape ({ [lineIdx]: { [catId]: ... } }) is still
// accepted so previously saved mobilists keep working.
//
// Either way the result is DEDUPLICATED by item: if Line 1 and Line 2
// both need the same blast frame it appears once on the mobilist
// (qty 1), with `lineIndexes` recording which lines use it.
export const collectSelectedEquipment = (selections, numPipelines = 1) => {
  const byKey = new Map();

  const addItem = (catId, groupName, item, lineIndex) => {
    const key = `${catId}|${groupName}|${item}`;
    if (!byKey.has(key)) {
      byKey.set(key, { catId, groupName, item, shared: false, lineIndexes: [] });
    }
    const entry = byKey.get(key);
    if (lineIndex === null || lineIndex === undefined) {
      entry.shared = true;
    } else if (!entry.lineIndexes.includes(lineIndex)) {
      entry.lineIndexes.push(lineIndex);
    }
  };

  const walkCategories = (categories, lineIndex) => {
    for (const [catId, groups] of Object.entries(categories || {})) {
      for (const [groupName, itemsSet] of Object.entries(groups || {})) {
        if (!itemsSet) continue;
        const items = Array.isArray(itemsSet) ? itemsSet : Array.from(itemsSet);
        items.forEach((item) => addItem(catId, groupName, item, lineIndex));
      }
    }
  };

  if (!selections) return [];

  if (selections.shared || selections.lines) {
    walkCategories(selections.shared, null);
    const lines = selections.lines || {};
    Object.keys(lines)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((lineIdx) => walkCategories(lines[lineIdx], Number(lineIdx)));
  } else {
    // Legacy shape: selections keyed directly by pipeline index.
    for (let pipeIdx = 0; pipeIdx < numPipelines; pipeIdx++) {
      walkCategories(selections[pipeIdx], pipeIdx);
    }
  }

  return [...byKey.values()];
};

// Human-readable scope for a deduplicated item, e.g. "Shared — all lines"
// or "Line 1, Line 3".
export const formatEquipmentScope = (entry) => {
  if (!entry) return '';
  if (entry.shared) return 'Shared — all lines';
  return entry.lineIndexes.map((idx) => `Line ${idx + 1}`).join(', ');
};

// Backup equipment: ONE "Backup Equipment" checkbox per section/group
// (keys `${catId}|${groupName}`). The selected items of a ticked section
// are mobilised as backup AS WELL AS plant equipment (an extra copy).
export const collectBackupEquipment = (selections, backupSelections, numPipelines = 1) => {
  const backupKeys = new Set(
    backupSelections ? Array.from(backupSelections) : [],
  );
  if (backupKeys.size === 0) return [];
  return collectSelectedEquipment(selections, numPipelines).filter(
    (entry) => backupKeys.has(`${entry.catId}|${entry.groupName}`),
  );
};

const costOfEquipment = (equipment) =>
  equipment.reduce((total, { groupName, item }) => {
    const isGeneric = ['CRCE Supply', 'Client Supply', 'Generic'].includes(item);
    const eqData = findEquipmentData(isGeneric ? groupName : item);
    return total + (eqData ? parseFloat(eqData['__EMPTY_1']) || 0 : 0);
  }, 0);

export const calculateTotalCost = (selections, numPipelines, backupSelections) => {
  // Cost each unique plant item once, plus each backup item once more
  // (backup equipment is an extra, costed copy of the selected items).
  return (
    costOfEquipment(collectSelectedEquipment(selections, numPipelines)) +
    costOfEquipment(collectBackupEquipment(selections, backupSelections, numPipelines))
  );
};

// Helper for cell styles
const setHeaderStyle = (cell, isRed = false) => {
  cell.font = { bold: true, size: 9 };
  cell.alignment = { horizontal: 'center', vertical: 'middle' };
  cell.border = {
    top: { style: 'thin' }, left: { style: 'thin' },
    bottom: { style: 'thin' }, right: { style: 'thin' }
  };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: isRed ? 'FFFF0000' : 'FF9FC5E8' } // Light blue or Red
  };
};

export const generateMobilistExcel = async (mobilist) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(mobilist.projectType || 'CCAB Offshore');

  // --- Column Widths ---
  sheet.getColumn(1).width = 12; // A: WBS #
  sheet.getColumn(2).width = 8;  // B: Ret/Con
  sheet.getColumn(3).width = 8;  // C: Type
  sheet.getColumn(4).width = 8;  // D: Photo
  sheet.getColumn(5).width = 8;  // E: CofO
  sheet.getColumn(6).width = 8;  // F: Unit Kg
  sheet.getColumn(7).width = 15; // G: Owner
  sheet.getColumn(8).width = 5;  // H: #
  sheet.getColumn(9).width = 45; // I: Description / Category
  sheet.getColumn(10).width = 15; // J: HS Code
  sheet.getColumn(11).width = 20; // K: SAP #
  sheet.getColumn(12).width = 10; // L: Quantity
  sheet.getColumn(13).width = 12; // M: Unit Cost
  sheet.getColumn(14).width = 12; // N: Total Cost
  sheet.getColumn(15).width = 30; // O: Notes

  // --- Top Information Block ---
  sheet.getCell('I3').value = 'CCAB - Closed Cycle Abrasive Blasting (Offshore)';
  sheet.getCell('I3').font = { bold: true, underline: true, size: 10 };

  // Project Info Left (A-B)
  sheet.getCell('A4').value = 'Enter Project # Here';
  sheet.getCell('A4').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
  sheet.getCell('A4').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  sheet.getCell('B4').value = mobilist.projectNumber || '';
  
  sheet.getCell('A5').value = 'For Pull WBS Element';
  sheet.getCell('A5').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF93C47D' } }; // Light Green
  sheet.getCell('A5').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  sheet.getCell('B5').value = mobilist.wbsElement || '';

  // Description / Type Legend (A-B)
  sheet.getCell('A7').value = 'Description Column';
  sheet.getCell('A7').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
  sheet.getCell('A7').font = { bold: true, size: 9 };
  sheet.getCell('A7').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };

  sheet.getCell('A8').value = 'Type';
  sheet.getCell('A8').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
  sheet.getCell('A8').font = { bold: true, size: 9 };
  sheet.getCell('A8').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  sheet.getCell('B8').value = 'TBC';
  sheet.getCell('B8').font = { color: { argb: 'FFFF0000' }, size: 9 };

  sheet.getCell('A9').value = 'FIC SOW (Qty & Size)';
  sheet.getCell('A9').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
  sheet.getCell('A9').font = { bold: true, size: 9 };
  sheet.getCell('A9').border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  sheet.getCell('B9').value = 'TBC';
  sheet.getCell('B9').font = { color: { argb: 'FFFF0000' }, size: 9 };

  // Pipe Specs — one column per line (Line 1, Line 2, ...), as in the
  // reference mobilist, instead of combining all lines into one cell.
  // Line columns start at J (column 10); the column after the last line
  // is the Total column (used by NUMBER OF PROJECTED FIELD JOINTS).
  const sowPipelines = mobilist.pipelines || [];
  const lineCount = Math.max(parseInt(mobilist.numberOfPipelines, 10) || 0, sowPipelines.length, 1);
  const linePipelines = Array.from({ length: lineCount }, (_, i) => sowPipelines[i] || {});
  const LINE_START_COL = 10; // J
  const lineCol = (idx) => LINE_START_COL + idx;
  const totalCol = LINE_START_COL + lineCount;

  const steelCutbacksOf = (p) =>
    p.steelCutbacksText ||
    (Array.isArray(p.steelCutbacks)
      ? p.steelCutbacks
          .map((e) =>
            typeof e === 'object' && e !== null
              ? e.value
                ? `${e.value}${e.tolerance ? ` ${e.tolerance}` : ''}`
                : `${e.primary}${e.secondary ? `/${e.secondary}` : ''}${e.tolerance !== '' && e.tolerance != null ? ` ${e.toleranceSign || '±'}${e.tolerance}` : ''}`
              : e,
          )
          .filter(Boolean)
          .join(', ')
      : p.steelCutbacks);

  // Column headers: Line 1 | Line 2 | ... | Total
  linePipelines.forEach((_, idx) => {
    const header = sheet.getCell(4, lineCol(idx));
    header.value = `Line ${idx + 1}`;
    header.font = { bold: true, size: 9, color: { argb: 'FF6B7280' } };
  });
  const totalHeader = sheet.getCell(4, totalCol);
  totalHeader.value = 'Total';
  totalHeader.font = { bold: true, size: 9, color: { argb: 'FF6B7280' } };

  const pipeSpecs = [
    { row: 5, label: 'PIPE STEEL OD', getValue: (p) => p.pipeSize },
    { row: 6, label: 'PARENT COATING', getValue: (p) => p.factoryCoatingType },
    { row: 7, label: 'CWC CUTBACKS', getValue: () => '' },
    { row: 8, label: 'STEEL CUTBACKS', getValue: steelCutbacksOf },
    { row: 9, label: 'PIPE WALL THICKNESS', getValue: (p) => p.wallThickness }
  ];
  pipeSpecs.forEach(spec => {
    sheet.getCell(`I${spec.row}`).value = spec.label;
    sheet.getCell(`I${spec.row}`).font = { bold: true, size: 9 };
    linePipelines.forEach((p, idx) => {
      const raw = spec.getValue(p);
      const text = raw === undefined || raw === null || raw === '' ? 'TBC' : String(raw);
      const valueCell = sheet.getCell(spec.row, lineCol(idx));
      valueCell.value = text;
      valueCell.font = text === 'TBC'
        ? { color: { argb: 'FFFF0000' }, size: 9, bold: true }
        : { size: 9, bold: true };
    });
  });

  // Project Specs (I-K)
  const specsStartRow = 11;
  sheet.getCell(`I${specsStartRow}`).value = 'PIPELAY MODE';
  sheet.getCell(`I${specsStartRow}`).font = { bold: true, size: 9 };
  sheet.getCell(specsStartRow, lineCol(0)).value = mobilist.pipelayMode?.toUpperCase() || mobilist.projectType?.toUpperCase() || 'OFFSHORE';
  sheet.getCell(specsStartRow, lineCol(0)).font = { bold: true, size: 9 };

  sheet.getCell(`I${specsStartRow+2}`).value = 'PRODUCTION DAYS';
  sheet.getCell(`I${specsStartRow+2}`).font = { bold: true, size: 9 };
  sheet.getCell(specsStartRow+2, lineCol(0)).value = mobilist.productionDays || '';
  sheet.getCell(specsStartRow+2, lineCol(0)).font = { bold: true, size: 9 };

  sheet.getCell(`I${specsStartRow+4}`).value = 'NUMBER OF PERSONNEL';
  sheet.getCell(`I${specsStartRow+4}`).font = { bold: true, size: 9 };
  sheet.getCell(specsStartRow+4, lineCol(0)).value = mobilist.numberOfPersonnel || mobilist.totalPersonnel || '';
  sheet.getCell(specsStartRow+4, lineCol(0)).font = { bold: true, size: 9 };

  // Number of field joints: per line in the line columns, with the
  // cumulative total in the Total column (as in the reference sheet).
  const jointsRow = specsStartRow + 6;
  sheet.getCell(`I${jointsRow}`).value = 'NUMBER OF PROJECTED FIELD JOINTS';
  sheet.getCell(`I${jointsRow}`).font = { bold: true, size: 9 };
  const totalJoints = linePipelines.reduce((acc, curr) => acc + (parseInt(curr.numberOfFieldJoints) || 0), 0);
  linePipelines.forEach((p, idx) => {
    const jointsCell = sheet.getCell(jointsRow, lineCol(idx));
    jointsCell.value = parseInt(p.numberOfFieldJoints, 10) || 0;
    jointsCell.font = { bold: true, size: 9 };
    jointsCell.alignment = { horizontal: 'center' };
  });
  const totalJointsCell = sheet.getCell(jointsRow, totalCol);
  totalJointsCell.value = totalJoints;
  totalJointsCell.font = { bold: true, size: 9 };
  totalJointsCell.alignment = { horizontal: 'center' };
  // "Totals" caption above the cumulative figure, as in the reference.
  const totalsCaption = sheet.getCell(jointsRow - 1, totalCol);
  totalsCaption.value = 'Totals';
  totalsCaption.font = { bold: true, size: 9, color: { argb: 'FF6B7280' } };
  totalsCaption.alignment = { horizontal: 'center' };

  // Project Info — LAST column (O), stacked and centred exactly as in
  // the reference sheet:
  //   REVISION: 0
  //   DATE ISSUED
  //   05.11.2026
  //   BY
  //   JAMIE TAYLOR
  const LAST_COL = 'O';
  const excelDate = (value) => {
    const d = value ? new Date(value) : new Date();
    if (Number.isNaN(d.getTime())) return 'TBC';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  };
  const setLastColCell = (row, value, font = { bold: true, size: 9 }) => {
    const cell = sheet.getCell(`${LAST_COL}${row}`);
    cell.value = value;
    cell.font = font;
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  };
  setLastColCell(4, 'REVISION: 0');
  setLastColCell(6, 'DATE ISSUED');
  setLastColCell(7, excelDate(mobilist.createdDate || mobilist.dateIssued));
  setLastColCell(8, 'BY');
  setLastColCell(9, (mobilist.createdBy || '').toUpperCase());

  // Status Legend — also in the last column, below the BY block,
  // headed "# Column" as in the reference sheet.
  const legRow = 11;
  setLastColCell(legRow, '# Column');
  
  const statuses = [
    { name: 'No Action', color: 'FF000000', fontColor: 'FFFFFFFF' }, // Black
    { name: 'On Order', color: 'FFFF0000' }, // Red
    { name: 'Picked or Delivered', color: 'FFFFFF00' }, // Yellow
    { name: 'Picked & Packed', color: 'FF00B050' }, // Green
    { name: 'Sourced Locally', color: 'FFCCFFCC' }, // Light Green
    { name: 'On Hold', color: 'FFFFFF00' } // Yellow
  ];
  statuses.forEach((status, idx) => {
    const cell = sheet.getCell(`${LAST_COL}${legRow + 1 + idx}`);
    cell.value = status.name;
    cell.font = { size: 9, bold: true, color: { argb: status.fontColor || 'FF000000' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: status.color } };
    cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
  });

  // Banners Row
  let currentRow = 20;
  
  // Left Banner
  sheet.mergeCells(`A${currentRow}:H${currentRow}`);
  const leftBanner = sheet.getCell(`A${currentRow}`);
  leftBanner.value = 'Supply Chain / Mobilisation (Only)';
  leftBanner.font = { bold: true, size: 9 };
  leftBanner.alignment = { horizontal: 'center', vertical: 'middle' };
  leftBanner.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF93C47D' } }; // Light Green
  leftBanner.border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'medium' }, right: { style: 'medium' } };

  // Right Banner
  sheet.mergeCells(`I${currentRow}:O${currentRow}`);
  const rightBanner = sheet.getCell(`I${currentRow}`);
  rightBanner.value = 'FIRING LINE';
  rightBanner.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
  rightBanner.alignment = { horizontal: 'center', vertical: 'middle' };
  rightBanner.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF0000' } }; // Red
  rightBanner.border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'medium' }, right: { style: 'medium' } };

  currentRow += 1;

  // --- Equipment Items ---
  // One row per unique selected item for the full SOW. ALL selected
  // equipment goes under a single PLANT EQUIPMENT heading — there is
  // no separate ON PIPE (or any other master-data category) section.
  const numPipes = parseInt(mobilist.numberOfPipelines, 10) || 1;
  const selectedEquipment = collectSelectedEquipment(mobilist.selections || {}, numPipes);

  const toExcelItem = (selected, isBackup = false) => {
    const { groupName, item } = selected;
    const isGeneric = ['CRCE Supply', 'Client Supply', 'Generic'].includes(item);
    const searchKey = isGeneric ? groupName : item;
    return {
      groupName,
      item,
      eqData: findEquipmentData(searchKey),
      searchKey,
      isGeneric,
      scope: formatEquipmentScope(selected),
      isBackup,
    };
  };

  const plantItems = selectedEquipment.map((selected) => toExcelItem(selected));

  // Backup equipment: the selected items of every section whose
  // "Backup Equipment" checkbox was ticked, repeated as an extra
  // costed copy under their own BACK UP EQUIPMENT heading.
  const backupItems = collectBackupEquipment(
    mobilist.selections || {},
    mobilist.backupSelections,
    numPipes,
  ).map((selected) => toExcelItem(selected, true));

  // --- Render Blocks: PLANT EQUIPMENT, then BACK UP EQUIPMENT ---
  const columns = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O'];

  const blocks = [];
  if (plantItems.length > 0) {
    blocks.push({ categoryName: 'PLANT EQUIPMENT', items: plantItems });
  }
  if (backupItems.length > 0) {
    blocks.push({ categoryName: 'BACK UP EQUIPMENT', items: backupItems });
  }

  for (const { categoryName, items } of blocks) {
    // Spacer row
    currentRow++;

    // Sub-Header Row
    const headerRow = sheet.getRow(currentRow);
    headerRow.values = [
      'WBS #', 'Ret/Con', 'Type', 'Photo', 'CofO', 'Unit Kg', 'Owner', '#', 
      categoryName, 'HS Code', 'SAP #', 'Quantity', 'Unit Cost £', 'Total Cost £', 'Notes'
    ];
    
    columns.forEach(col => setHeaderStyle(sheet.getCell(`${col}${currentRow}`)));
    currentRow++;

    let blockStartRow = currentRow;

    // Data Rows
    items.forEach(({ eqData, searchKey, isGeneric, item, scope, isBackup }) => {
      const price = eqData ? parseFloat(eqData['__EMPTY_1']) || 0 : 0;
      const qty = 1; // deduplicated: one per unique item, however many lines use it
      const notes = [
        isBackup ? 'Backup Equipment' : '',
        scope ? scope : '',
        item === 'Client Supply' ? 'CRCE or Client Supply?' : '',
      ].filter(Boolean).join('; ');
      const total = price * qty;
      const owner = eqData ? eqData['__EMPTY_8'] : '';
      
      const row = sheet.getRow(currentRow);
      row.values = [
        mobilist.wbsElement || '',               // A: WBS
        eqData ? eqData['__EMPTY_4'] : '',       // B: Ret/Con
        eqData ? eqData['__EMPTY_5'] : '',       // C: Type
        '',                                      // D: Photo
        '',                                      // E: CofO
        '',                                      // F: Unit Kg
        owner,                                   // G: Owner
        '',                                      // H: #
        eqData ? eqData['£ KEY'] : searchKey,    // I: Description
        eqData ? eqData['__EMPTY_2'] : '',       // J: HS Code
        eqData ? eqData['__EMPTY'] : '',         // K: SAP #
        qty,                                     // L: Quantity
        price,                                   // M: Unit Cost
        total,                                   // N: Total Cost
        notes                                    // O: Notes
      ];

      // Formatting
      row.getCell(13).numFmt = '0.00'; // Unit Cost
      row.getCell(14).numFmt = '0.00'; // Total Cost

      // Styling based on data
      if (owner && owner.toLowerCase().includes('workshop')) {
        row.getCell(7).font = { color: { argb: 'FFFF0000' }, size: 9 };
      } else {
        row.getCell(7).font = { size: 9 };
      }

      if (item === 'Client Supply') {
        row.getCell(15).font = { color: { argb: 'FFFF0000' }, size: 9 };
      } else {
        row.getCell(15).font = { size: 9 };
      }

      // Base font and borders
      columns.forEach(col => {
        const cell = sheet.getCell(`${col}${currentRow}`);
        cell.font = cell.font || { size: 9 };
        cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        
        if (['A', 'B', 'C', 'G', 'L', 'M', 'N'].includes(col)) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          cell.alignment = { vertical: 'middle' };
        }
      });

      currentRow++;
    });

    let blockEndRow = currentRow - 1;

    // Sub Category Total Row
    sheet.mergeCells(`I${currentRow}:M${currentRow}`);
    const subTotalLabel = sheet.getCell(`I${currentRow}`);
    subTotalLabel.value = 'Sub Category Total ->';
    subTotalLabel.font = { bold: true, color: { argb: 'FFFF0000' }, size: 9 };
    subTotalLabel.alignment = { horizontal: 'right' };
    subTotalLabel.border = { bottom: { style: 'medium', color: { argb: 'FFFF0000' } } };
    
    // Fill the empty cells before SubTotal with bottom red border
    for(let i = 1; i <= 8; i++) {
      sheet.getCell(currentRow, i).border = { bottom: { style: 'medium', color: { argb: 'FFFF0000' } } };
    }

    const subTotalValue = sheet.getCell(`N${currentRow}`);
    subTotalValue.value = { formula: `SUM(N${blockStartRow}:N${blockEndRow})` };
    subTotalValue.numFmt = '0.00';
    subTotalValue.font = { bold: true, color: { argb: 'FFFF0000' }, size: 9 };
    subTotalValue.alignment = { horizontal: 'center' };
    subTotalValue.border = { bottom: { style: 'medium', color: { argb: 'FFFF0000' } } };

    const notesTotal = sheet.getCell(`O${currentRow}`);
    notesTotal.border = { bottom: { style: 'medium', color: { argb: 'FFFF0000' } } };

    currentRow++;
  }

  // --- Generate and Download ---
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `Mobilist_${mobilist.projectName || 'Export'}.xlsx`;
  a.click();
  
  URL.revokeObjectURL(url);
};
